// Chooser-paradigm image field. Two states driven solely by `value`:
// empty (Upload + Browse + Paste-URL disclosure) and filled (thumbnail
// + filename + Change + Clear). "Change image" toggles a transient
// `changing` mode that re-surfaces the chooser UI without clearing the
// value, so Cancel can revert to the original thumbnail.
//
// **Bypasses `<FieldShell>`** (precedent: `RichTextField`) — no
// canonical focusable `<input>` to associate `htmlFor` with. The label
// renders as a labelledby span; the Paste-URL `<input>` carries the
// `aria-describedby` / `aria-invalid` wiring when present.
//
// **Clear → undo toast.** Auto-dismiss is the recovery window; no
// inline confirm modal — matches the no-confirm-but-recoverable
// pattern Banks's audit batches have favored.
//
// **P-037.31 §A — Clear fires `onChange(undefined)`, not `onChange('')`.**
// Block imageUrl fields are declared `z.string().url().describe('imageUrl').optional()`;
// `''` fails the `.url()` check and surfaces "Invalid url" + breaks the
// preview render. `undefined` flows cleanly through the form's `writeField`
// merge ({ ...obj, [name]: undefined }) and Zod's `.optional()` accepts
// the resulting "key present with undefined value" identically to "key
// absent" — preview renderer's `imageUrl ?? FALLBACK_IMAGE_SRC` arm then
// kicks in. Same boundary-coercion pattern as `renderSelect` (FieldRenderer
// maps optional-select `''` → undefined for the same Zod-rejection reason).
//
// **P-037.31 §B — Min-size validation + hint.** When the field's
// per-Block image target is registered, the resolved `@2x retina target`
// dims serve two surfaces: a "Minimum size: W × H px" hint (rendered
// below the label, above the chooser buttons) AND a pre-upload guard at
// the paste-URL submit path. The Upload modal owns the file-picker arm's
// guard (same registry-derived target threaded via `target` prop). Both
// guards REJECT — surface a toast like "Image must be at least W × H
// pixels (uploaded: w × h)" + keep the field's value untouched. Existing
// too-small uploads stay; validation fires only at NEW upload time.

import { useEffect, useRef, useState } from 'preact/hooks'

import { toRetinaTarget, type ImageTarget } from '../../../lib/image-dimensions'
import { validateImageSize, tooSmallMessage } from '../../../lib/image-resize'
import {
  secondaryButtonClassCompactSm,
  secondaryButtonClassCompactSmWithDisabled,
} from '../../button-classes'
import { addToast } from '../../toast/toast-store'
import { createLazyComponentLoader } from './lazy-component'
import type { LibraryBrowserModal as LibraryBrowserModalComponent } from './library-browser-modal'
import type { UploadModal as UploadModalComponent } from './upload-modal'

type UploadModalType = typeof UploadModalComponent
type LibraryBrowserModalType = typeof LibraryBrowserModalComponent

const uploadModalLoader = createLazyComponentLoader(
  () => import('./upload-modal'),
  (m) => m.UploadModal,
)
const libraryBrowserModalLoader = createLazyComponentLoader(
  () => import('./library-browser-modal'),
  (m) => m.LibraryBrowserModal,
)

export const resetUploadModalCacheForTest = uploadModalLoader.resetForTest
export const resetLibraryBrowserModalCacheForTest = libraryBrowserModalLoader.resetForTest

const THUMB_PX = 120
const SUBTLE_LINK_CLASS =
  'self-start text-xs text-gray-600 underline underline-offset-2 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500'

interface ImageUrlFieldProps {
  readonly id: string
  readonly name: string
  readonly label: string
  readonly helpText?: string
  readonly value: string
  // P-037.31 §A — `undefined` is the empty sentinel (was `''`).
  // `handleClear` fires undefined so the form's writeField merge
  // produces `{ ...obj, [name]: undefined }` — Zod `.optional()`
  // accepts it; `''` would fail `.url()`.
  readonly onChange: (next: string | undefined) => void
  readonly onBlur?: () => void
  readonly error?: string
  readonly required?: boolean
  readonly target?: ImageTarget
  readonly blockId?: string
}

export function ImageUrlField({
  id,
  name,
  label,
  helpText,
  value,
  onChange,
  onBlur,
  error,
  required = false,
  target,
  blockId,
}: ImageUrlFieldProps) {
  const [Upload, setUpload] = useState<UploadModalType | null>(() => uploadModalLoader.get())
  const [Browse, setBrowse] = useState<LibraryBrowserModalType | null>(() => libraryBrowserModalLoader.get())
  const [uploadOpen, setUploadOpen] = useState(false)
  const [browseOpen, setBrowseOpen] = useState(false)
  const [changing, setChanging] = useState(false)
  const [pasteOpen, setPasteOpen] = useState(false)
  const [pasteDraft, setPasteDraft] = useState('')
  const unmountedRef = useRef(false)
  useEffect(() => () => { unmountedRef.current = true }, [])

  // Reset chooser sub-state when value transitions (successful pick,
  // Clear, JSON-load). The skip-first-run guard avoids 3 no-op
  // setStates on mount when the field opens with a non-empty value
  // (the common case — editing a saved Email).
  const valueDidMountRef = useRef(false)
  useEffect(() => {
    if (!valueDidMountRef.current) {
      valueDidMountRef.current = true
      return
    }
    setChanging(false)
    setPasteOpen(false)
    setPasteDraft('')
  }, [value])

  const openUpload = async () => {
    if (Upload === null) {
      const Loaded = await uploadModalLoader.load()
      if (unmountedRef.current) return
      setUpload(() => Loaded)
    }
    setUploadOpen(true)
  }

  const openBrowse = async () => {
    if (Browse === null) {
      const Loaded = await libraryBrowserModalLoader.load()
      if (unmountedRef.current) return
      setBrowse(() => Loaded)
    }
    setBrowseOpen(true)
  }

  // P-037.31 §B — pre-compute the retina (`@2x`) target once per render.
  // Same value drives the hint UI + the paste-URL min-size guard. The
  // file-picker arm's guard lives in `<UploadModal>` (target prop already
  // threaded). The `target` prop is always defined when the field
  // renders via FieldRenderer's renderImageUrl (the form generator
  // calls `getImageTarget()` which returns the catch-all DEFAULT_TARGET
  // 600 × 600 for any unmapped key — never undefined). The undefined
  // guard here covers direct-mount callers (tests, future host
  // components) that may omit the prop entirely; those skip both the
  // hint and the validation.
  const retinaTarget = target !== undefined ? toRetinaTarget(target) : undefined

  const handleUploadSubmit = (url: string) => { onChange(url); setUploadOpen(false) }
  const handleBrowseSelect = (url: string) => { onChange(url); setBrowseOpen(false) }

  const handlePasteSave = async () => {
    const trimmed = pasteDraft.trim()
    if (trimmed === '') return
    // P-037.31 §B — min-size guard. Fetch the URL, measure natural dims
    // via createImageBitmap, reject if EITHER axis is smaller than the
    // @2x retina target. Network/decode errors degrade silently (proceed
    // with the paste) — the paste-URL arm is a power-user escape hatch,
    // not the primary upload surface; we don't want a CORS-blocked CDN
    // to lock staff out of using a URL they trust.
    if (retinaTarget !== undefined) {
      try {
        const resp = await fetch(trimmed)
        if (!resp.ok) throw new Error(`Fetch failed: ${resp.status}`)
        const blob = await resp.blob()
        const result = await validateImageSize(blob, retinaTarget)
        if (!result.ok) {
          addToast({
            kind: 'error',
            message: tooSmallMessage(result.srcWidth, result.srcHeight, retinaTarget),
          })
          return
        }
      } catch {
        // Silent degrade — proceed with the paste. See above rationale.
      }
    }
    onChange(trimmed)
  }

  const handleClear = () => {
    const prev = value
    // P-037.31 §A — fire undefined (was ''). The empty string fails
    // the Zod `.url()` validation on optional imageUrl fields;
    // undefined flows through writeField cleanly and `.optional()`
    // accepts it. See file header for the full rationale.
    onChange(undefined)
    addToast({
      kind: 'info',
      message: 'Image cleared.',
      action: { label: 'Undo', onClick: () => onChange(prev) },
    })
  }

  const isFilled = value !== ''
  const showChooser = !isFilled || changing
  const showThumbnail = isFilled && !changing
  const filename = isFilled ? deriveFilename(value) : ''
  const helpId = helpText !== undefined ? `${id}-help` : undefined
  const errorId = error !== undefined ? `${id}-error` : undefined
  const describedBy =
    [helpId, errorId].filter((x): x is string => x !== undefined).join(' ') || undefined
  const labelId = `${id}-label`

  return (
    <div class="flex flex-col gap-2" aria-labelledby={labelId} role="group">
      <span id={labelId} class="text-sm font-medium text-gray-800">
        {label}
        {required && (
          <span class="ml-0.5 text-red-600" aria-hidden="true">*</span>
        )}
      </span>

      {showThumbnail && (
        <div class="flex items-start gap-3">
          <Thumbnail url={value} />
          <div class="flex min-w-0 flex-1 flex-col gap-2">
            <p class="m-0 truncate text-xs font-medium text-gray-900" title={filename}>
              {filename}
            </p>
            <div class="flex flex-wrap items-center gap-2 text-xs">
              <button
                type="button"
                onClick={() => setChanging(true)}
                class={secondaryButtonClassCompactSm}
              >
                Change image
              </button>
              <button
                type="button"
                onClick={handleClear}
                class={secondaryButtonClassCompactSm}
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      )}

      {showChooser && retinaTarget !== undefined && (
        // P-037.31 §B — min-size hint. Placement: below the labelledby
        // span, above the Upload/Browse/Paste-URL controls. Compact
        // form `Minimum size: W × H px` (Banks-confirmed format). The
        // dimensions match the pre-upload guard's threshold so the
        // staff-facing copy + the rejection toast agree.
        <p class="m-0 text-xs text-gray-600" data-testid="image-min-size-hint">
          Minimum size: {retinaTarget.width} × {retinaTarget.height} px
        </p>
      )}

      {showChooser && (
        <div class="flex flex-col gap-2">
          <div class="flex flex-wrap items-center gap-3 text-xs">
            <button type="button" onClick={openUpload} class={secondaryButtonClassCompactSm}>
              Upload new
            </button>
            <button type="button" onClick={openBrowse} class={secondaryButtonClassCompactSm}>
              {/* P-037.30 ninth post-Phase-Close §A — Browse button label
                  uses the per-type label (sourced from `target.label` via
                  the IMAGE_TARGETS registry at the field-renderer layer)
                  so it stays consistent with the modal title
                  (`${typeLabel} Library`). Falls back to "Browse Library"
                  when the field has no type-label override (only hit on
                  the DEFAULT_TARGET fallback path). The earlier hard-
                  coded "Browse chapter library" was inconsistent with
                  the seventh-batch §C modal-title rewrite. */}
              {target?.label !== undefined ? `Browse ${target.label} Library` : 'Browse Library'}
            </button>
            {changing && (
              <button type="button" onClick={() => setChanging(false)} class={SUBTLE_LINK_CLASS}>
                Cancel
              </button>
            )}
          </div>
          {pasteOpen ? (
            <div class="flex flex-wrap items-center gap-2">
              <input
                id={id}
                name={name}
                type="url"
                value={pasteDraft}
                onInput={(e) => setPasteDraft((e.currentTarget as HTMLInputElement).value)}
                onBlur={onBlur}
                aria-label="Paste image URL"
                {...(describedBy !== undefined ? { 'aria-describedby': describedBy } : {})}
                {...(error !== undefined ? { 'aria-invalid': true as const } : {})}
                placeholder="https://…"
                class="min-w-[12rem] flex-1 rounded border border-gray-300 px-2 py-1 text-xs text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={handlePasteSave}
                disabled={pasteDraft.trim() === ''}
                class={secondaryButtonClassCompactSmWithDisabled}
              >
                Save
              </button>
              <button
                type="button"
                onClick={() => { setPasteOpen(false); setPasteDraft('') }}
                class={SUBTLE_LINK_CLASS}
              >
                Cancel
              </button>
            </div>
          ) : (
            <button type="button" onClick={() => setPasteOpen(true)} class={SUBTLE_LINK_CLASS}>
              Paste URL instead
            </button>
          )}
        </div>
      )}

      {helpText !== undefined && (
        <p id={helpId} class="m-0 text-xs text-gray-600">{helpText}</p>
      )}
      {error !== undefined && (
        <p id={errorId} class="m-0 text-xs text-red-700" role="alert">{error}</p>
      )}

      {Upload !== null && (
        <Upload
          open={uploadOpen}
          onSubmit={handleUploadSubmit}
          onCancel={() => setUploadOpen(false)}
          {...(target !== undefined ? { target } : {})}
          {...(blockId !== undefined ? { blockId, fieldName: name } : {})}
        />
      )}
      {Browse !== null && (
        <Browse
          open={browseOpen}
          onSelect={handleBrowseSelect}
          onCancel={() => setBrowseOpen(false)}
          {...(blockId !== undefined ? { defaultScope: { blockId, fieldName: name } } : {})}
          {...(target?.label !== undefined ? { typeLabel: target.label } : {})}
        />
      )}
    </div>
  )
}

function Thumbnail({ url }: { url: string }) {
  const [errored, setErrored] = useState(false)
  // Reset error state when the url transitions — skip first run to
  // avoid a no-op setState on mount.
  const urlDidMountRef = useRef(false)
  useEffect(() => {
    if (!urlDidMountRef.current) {
      urlDidMountRef.current = true
      return
    }
    setErrored(false)
  }, [url])
  if (errored) {
    return (
      <div
        class="flex shrink-0 flex-col items-center justify-center rounded border border-dashed border-gray-300 bg-gray-50 text-center text-[10px] leading-tight text-gray-600"
        style={{ width: THUMB_PX, height: THUMB_PX }}
        role="img"
        aria-label="Image failed to load"
      >
        <span>Image failed</span>
        <span>to load</span>
      </div>
    )
  }
  return (
    <img
      src={url}
      alt=""
      loading="lazy"
      onError={() => setErrored(true)}
      class="shrink-0 rounded border border-gray-200 object-cover"
      style={{ width: THUMB_PX, height: THUMB_PX }}
    />
  )
}

// Firebase Storage download URLs encode the bucket path inside the
// last pathname segment (e.g. `.../o/uploads%2Fcommentary%2Fphoto.jpg`
// → 'uploads%2Fcommentary%2Fphoto.jpg'). Decoding + re-splitting
// surfaces just the file basename. Plain CDN URLs (no encoded slashes)
// flow through with the decoded value identical to the input — the
// re-split is a safe no-op.
function deriveFilename(url: string): string {
  try {
    const parsed = new URL(url)
    const lastSegment = parsed.pathname.split('/').filter(Boolean).pop() ?? ''
    const decoded = decodeURIComponent(lastSegment)
    return decoded.split('/').filter(Boolean).pop() ?? url
  } catch {
    return url
  }
}
